"use client";

import React, { forwardRef, useCallback, useMemo, useState } from "react";
import { useContainerIsMobile } from "../../hooks/useContainerIsMobile";
import { activeHidden } from "../common/BlockVisibility";
import { defaultRenderImage } from "../../utils/image/renderImage";
import { interpolate } from "../../utils/interpolate";
import {
  ContentColumn,
  Header,
  HeaderText,
  HighlightsSection,
  Inner,
  SectionDescription,
  SectionTitle,
  toSectionPadding,
} from "./DynamicHighlights.styles";
import type { BentoSpan } from "./bentoLayout";
import { filterModalItems, resolveHighlightItems } from "./itemsResolver";
import { HighlightCard } from "./parts/HighlightCard";
import { HighlightModal } from "./parts/HighlightModal";
import { HighlightsCarousel } from "./parts/HighlightsCarousel";
import { HighlightsGrid } from "./parts/HighlightsGrid";
import {
  DEFAULT_AUTOPLAY_SPEED,
  ITEM_GAP,
  type DynamicHighlightsProps,
  type HighlightItem,
} from "./types";

const DynamicHighlightsInner = (
  {
    htmlId,
    padding,
    title,
    titleTag = "h2",
    titleVisible = true,
    description,
    descriptionVisible = true,
    headerAlign = { desktop: "start", mobile: "start" },

    highlightsField,
    fieldMap,
    showTitle: showItemTitle = true,
    showDescription = true,
    // `h3` porque o cabeçalho da seção é `h2`: o card fica um nível abaixo.
    itemTitleTag = "h3",
    itemDescriptionTag = "p",
    detailsLabel,
    detailsVariant = "link",

    modalEnabled = true,
    modalBgColor,
    modalTitleColor,
    modalTextColor,
    modalIconColor,

    backgroundType = "color",
    bgColor,
    cardBgColor,
    cardBorderColor,
    titleColor,
    descriptionColor,
    itemTitleColor,
    itemDescriptionColor,
    detailsLinkColor,
    overlayColor,
    navigationColor,

    format = "overlay",
    presentation = "carousel",
    gridArrangement = "uniform",
    gridColumns = 2,
    carouselItemsPerPage = 3,
    maxItems = 0,
    navigation = "arrowsAndIndicator",
    arrowType = "outline",
    indicatorType = "bar",
    carouselLoop = false,
    carouselAutoplay = false,
    carouselAutoplaySpeed = DEFAULT_AUTOPLAY_SPEED,
    carouselWidth = "content",
    imageAspect = "fixed",
    cardHeight = { desktop: "medium", mobile: "medium" },
    gridShowAsCarouselOnMobile = true,

    dynamicStructure,
    isEditing,
    renderImage = defaultRenderImage,

    activePalette,
    hideOnMobile,
    hideOnDesktop,
  }: DynamicHighlightsProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  // Mobile é medido pelo PRÓPRIO bloco (nunca `matchMedia`) — é o que funciona
  // no preview mobile do builder, que restringe a largura do canvas.
  const { ref: measureRef, isMobile } = useContainerIsMobile();
  const setSectionRef = useCallback(
    (node: HTMLDivElement | null) => {
      measureRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [measureRef, ref],
  );

  const items = useMemo(
    () => resolveHighlightItems({ dynamicStructure, highlightsField, fieldMap, maxItems }),
    [dynamicStructure, highlightsField, fieldMap, maxItems],
  );

  // Só os itens com descrição completa entram na navegação do modal.
  const modalItems = useMemo(() => filterModalItems(items), [items]);
  const [modalIndex, setModalIndex] = useState<number | null>(null);

  /*
   * O afeto de "abre detalhes" (link "Ver mais", cursor, foco) vale TAMBÉM no
   * builder — é o que o Champion configurou e precisa ver.
   */
  const modalActive = modalEnabled && modalItems.length > 0;

  /*
   * Só a EDIÇÃO bloqueia a abertura do modal (no canvas, clicar seleciona o
   * bloco). A previsualização também é builder, e ali o modal precisa abrir —
   * por isso o guard é `isEditing`, nunca `isBuilder`.
   */
  const openModal = useCallback(
    (id: string) => {
      if (isEditing) return;
      const index = modalItems.findIndex((item) => item.id === id);
      if (index >= 0) setModalIndex(index);
    },
    [isEditing, modalItems],
  );
  const closeModal = useCallback(() => setModalIndex(null), []);

  // Sem itens o bloco não renderiza — o wrapper da ui cuida do aviso no builder.
  if (items.length === 0) return null;

  const columns = Math.min(Math.max(gridColumns, 2), 4);
  const perPage = Math.min(Math.max(carouselItemsPerPage, 1), 3);
  /*
   * A navegação segue o TÍTULO da paleta (`--text-title`), não um cinza fixo:
   * `#334155` sumiria nas paletas de fundo escuro, onde o título é branco. É
   * cromo neutro sem ser cor de marca — em `--general-primary` ela competiria
   * com os CTAs do card.
   */
  const align = isMobile ? headerAlign.mobile : headerAlign.desktop;

  const navColor = navigationColor || "var(--text-title)";

  // Espaçamento fixo por device (ver ITEM_GAP): 24px no desktop, 16px no mobile.
  const itemGap = isMobile ? ITEM_GAP.mobile : ITEM_GAP.desktop;

  const isCarousel =
    presentation === "carousel" ||
    (presentation === "grid" && isMobile && gridShowAsCarouselOnMobile);

  const isClickable = (item: HighlightItem) =>
    modalActive && (item.extendedDescription || "").trim() !== "";

  const renderCard = (item: HighlightItem, span: BentoSpan = "minor", single = false) => (
    <HighlightCard
      item={item}
      format={format}
      span={span}
      imageAspect={imageAspect}
      cardHeight={cardHeight.desktop}
      cardHeightMobile={cardHeight.mobile}
      single={single}
      isMobile={isMobile}
      colors={{
        cardBgColor,
        cardBorderColor,
        itemTitleColor,
        itemDescriptionColor,
        detailsLinkColor,
        overlayColor,
      }}
      showTitle={showItemTitle}
      showDescription={showDescription}
      itemTitleTag={itemTitleTag}
      itemDescriptionTag={itemDescriptionTag}
      clickable={isClickable(item)}
      detailsLabel={detailsLabel}
      detailsVariant={detailsVariant}
      onOpen={openModal}
      renderImage={renderImage}
    />
  );

  /*
   * Título e descrição do cabeçalho aceitam variáveis do registro
   * (`{{item.campo|tipo}}`) — como os outros blocos dinâmicos, o valor passa
   * pelo `interpolate` antes de exibir, senão o token sairia literal. `.text`
   * decide a visibilidade (vazio depois de resolver = não renderiza).
   */
  const safeStructure = dynamicStructure ?? {};
  const titleInterpolated = interpolate({ structure: safeStructure, template: title || "" });
  const descriptionInterpolated = interpolate(
    { structure: safeStructure, template: description || "" },
    { parseHtml: true },
  );
  const showTitle = titleVisible && !!titleInterpolated.text.trim();
  const showDescriptionHeader = descriptionVisible && !!descriptionInterpolated.text.trim();

  /*
   * O cabeçalho e as setas do carrossel dividem a MESMA linha (padrão do bloco
   * 38), então quem monta o cabeçalho recebe as setas prontas. Na grade, e com
   * o cabeçalho centralizado, `arrows` vem `null`.
   */
  const renderHeader = (arrows: React.ReactNode | null) => {
    if (!showTitle && !showDescriptionHeader && !arrows) return null;

    return (
      <Header align={align}>
        <HeaderText align={align}>
          {showTitle && (
            <SectionTitle titleTag={titleTag as never} $color={titleColor} data-af-field="title">
              {titleInterpolated.node}
            </SectionTitle>
          )}
          {showDescriptionHeader && (
            // `div` porque a descrição passa por `parseHtml` (pode virar parágrafo/
            // lista) — texto rico não pode morar dentro de `p`/heading (ATW-414).
            <SectionDescription titleTag="div" $color={descriptionColor} data-af-field="description">
              {descriptionInterpolated.node}
            </SectionDescription>
          )}
        </HeaderText>
        {arrows}
      </Header>
    );
  };

  return (
    <HighlightsSection
      ref={setSectionRef}
      id={htmlId}
      data-testid="dynamic-highlights"
      className={`dynamic-highlights-section ${activePalette || ""} ${activeHidden({ hideOnMobile, hideOnDesktop })}`}
      padding={toSectionPadding(padding)}
      backgroundcolor={backgroundType === "color" ? bgColor : undefined}
    >
      <ContentColumn>
        {/* `data-anim-stagger` num container INTERNO — nunca no root. */}
        <Inner data-anim-stagger>
          {!isCarousel && renderHeader(null)}

          {isCarousel ? (
            <HighlightsCarousel
              items={items}
              itemsPerPage={perPage}
              gap={itemGap}
              isMobile={isMobile}
              loop={carouselLoop}
              carouselWidth={carouselWidth}
              autoplay={carouselAutoplay}
              autoplaySpeed={carouselAutoplaySpeed}
              navigation={navigation}
              arrowType={arrowType}
              indicatorType={indicatorType}
              navigationColor={navColor}
              align={align}
              renderHeader={renderHeader}
              renderItem={(item, single) => renderCard(item, "minor", single)}
            />
          ) : (
            <HighlightsGrid
              items={items}
              // No mobile sem "virar carrossel", a grade uniforme e o bento caem
              // numa coluna só (o bento com o grande primeiro, ordem do DOM).
              arrangement={isMobile ? "uniform" : gridArrangement}
              columns={isMobile ? 1 : columns}
              gap={itemGap}
              /*
               * Grade de 2 colunas: a célula é quase tão larga quanto o card
               * sozinho do carrossel, e a proporção retrato de "Alta" pedia
               * mais altura do que o teto (70vh) permite — a imagem parava
               * antes do fim do card e sobrava faixa vazia. Reusa a escala
               * menos extrema do card único, que existe por esse mesmo motivo.
               */
              renderItem={(item, span) => renderCard(item, span, !isMobile && columns <= 2)}
            />
          )}
        </Inner>
      </ContentColumn>

      {modalActive && (
        <HighlightModal
          items={modalItems}
          index={modalIndex}
          activePalette={activePalette}
          arrowType={arrowType}
          indicatorType={indicatorType}
          navigationColor={navColor}
          onClose={closeModal}
          onNavigate={setModalIndex}
          bgColor={modalBgColor}
          titleColor={modalTitleColor}
          textColor={modalTextColor}
          iconColor={modalIconColor}
          renderImage={renderImage}
        />
      )}
    </HighlightsSection>
  );
};

export const DynamicHighlights = forwardRef<HTMLDivElement, DynamicHighlightsProps>(
  DynamicHighlightsInner,
);

export type { DynamicHighlightsProps };
